import { useState } from "react";
import axios from "axios";
import { Link2, Plus, X, Loader2, ArrowRight, Check } from "lucide-react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Link } from "react-router-dom";
import { startCheckout } from "@/lib/payments";
import { track } from "@/lib/ebook";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const MAX_LINKS = 10;

const STEPS = [
  { id: 1, label: "Your Details" },
  { id: 2, label: "Photo Links" },
  { id: 3, label: "The Vision" },
  { id: 4, label: "Confirm & Pay" },
];

export default function PhotoSubmissionForm({
  packageId = "sovereign",
  checkoutPackageId = "sovereign-start",
}) {
  const [step, setStep] = useState(1);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [occasion, setOccasion] = useState("");
  const [notes, setNotes] = useState("");
  const [links, setLinks] = useState([""]);
  const [submitting, setSubmitting] = useState(false);

  const setLinkAt = (i, v) => {
    const next = [...links]; next[i] = v; setLinks(next);
  };
  const addLink = () => {
    if (links.length >= MAX_LINKS) return;
    setLinks([...links, ""]);
  };
  const removeLink = (i) => {
    const next = links.filter((_, idx) => idx !== i);
    setLinks(next.length ? next : [""]);
  };
  const activeLinks = links.map((l) => l.trim()).filter(Boolean);

  const goNext = () => {
    if (step === 1 && (!name || !email || !phone)) { toast.error("Complete your details."); return; }
    if (step === 2 && activeLinks.length === 0) { toast.error("Add at least one photo link."); return; }
    if (step === 2) {
      const bad = activeLinks.find((l) => !/^https?:\/\//i.test(l));
      if (bad) { toast.error("Links must start with http(s)://"); return; }
    }
    setStep((s) => Math.min(STEPS.length, s + 1));
  };
  const goBack = () => setStep((s) => Math.max(1, s - 1));

  const handleSubmitAndPay = async () => {
    setSubmitting(true);
    try {
      await axios.post(`${API}/photo-submissions`, {
        name, email, phone, package_id: packageId,
        occasion, notes, photo_links: activeLinks,
      });
      track("photo_submission_created", packageId, { count: activeLinks.length });
      await startCheckout(checkoutPackageId, { email, name, source: `photo-submission-${packageId}` });
    } catch (err) {
      toast.error(err?.response?.data?.detail || err?.message || "Submission failed.");
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="photo-submission-form" className="border border-white/10 bg-black/40 backdrop-blur-xl">
      <div className="grid grid-cols-4 border-b border-white/10">
        {STEPS.map((s) => {
          const active = step === s.id;
          const done = step > s.id;
          return (
            <div key={s.id} data-testid={`step-indicator-${s.id}`}
              className={`p-4 md:p-5 text-center border-r border-white/5 last:border-r-0 transition-colors ${active ? "bg-[#824937]/20" : ""}`}>
              <div className={`text-[10px] uppercase tracking-[0.22em] ${active || done ? "text-[#D4AF37]" : "text-white/40"}`}>
                Step {String(s.id).padStart(2, "0")}
              </div>
              <div className={`mt-1 text-xs md:text-sm font-serif-display ${active || done ? "text-white" : "text-white/40"}`}>
                {s.label}
              </div>
            </div>
          );
        })}
      </div>

      <div className="p-7 md:p-10 space-y-6 min-h-[360px]">
        {step === 1 && (
          <div className="space-y-5">
            <p className="overline">Step 1 — Your Details</p>
            <h3 className="font-serif-display text-2xl md:text-3xl leading-tight">Let&apos;s start with you.</h3>
            <div className="grid sm:grid-cols-2 gap-5">
              <Field label="Full name">
                <Input required data-testid="ps-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="Your name" />
              </Field>
              <Field label="Email">
                <Input required type="email" data-testid="ps-email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@email.com" />
              </Field>
            </div>
            <Field label="Phone">
              <Input required data-testid="ps-phone" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="+1 555 000 0000" />
            </Field>
          </div>
        )}

        {step === 2 && (
          <div className="space-y-5">
            <p className="overline">Step 2 — Photo Links</p>
            <h3 className="font-serif-display text-2xl md:text-3xl leading-tight">Send us your photo links.</h3>
            <p className="text-white/60 text-sm">
              Paste shareable links to your photos — Google Drive, Dropbox, iCloud, or any public URL. Up to {MAX_LINKS} links.
              {" "}<Link to="/photo-guide" className="gold-link">See our standards →</Link>
            </p>
            <div className="space-y-3" data-testid="ps-links">
              {links.map((l, i) => (
                <div key={i} className="flex items-center gap-2">
                  <div className="w-10 h-10 flex-shrink-0 border border-white/15 bg-black/40 flex items-center justify-center">
                    <Link2 className="w-4 h-4 text-[#D4AF37]" />
                  </div>
                  <Input
                    data-testid={`ps-link-${i}`}
                    value={l}
                    onChange={(e) => setLinkAt(i, e.target.value)}
                    placeholder="https://drive.google.com/…"
                    className="bg-black border-white/15 h-11 rounded-none text-white focus-visible:ring-1 focus-visible:ring-[#D4AF37] focus-visible:border-[#D4AF37]"
                  />
                  {links.length > 1 && (
                    <button type="button" onClick={() => removeLink(i)} data-testid={`ps-remove-${i}`}
                      className="w-10 h-10 flex-shrink-0 text-white/50 hover:text-[#D4AF37] hover:bg-white/5 flex items-center justify-center" aria-label="Remove">
                      <X className="w-4 h-4" />
                    </button>
                  )}
                </div>
              ))}
              {links.length < MAX_LINKS && (
                <button type="button" onClick={addLink} data-testid="ps-add-link"
                  className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.22em] text-[#D4AF37]/80 hover:text-[#D4AF37] transition-colors">
                  <Plus className="w-3.5 h-3.5" /> Add another link
                </button>
              )}
            </div>
            <p className="text-white/40 text-xs">
              Make sure your Drive / Dropbox links are set to &quot;anyone with the link can view.&quot;
            </p>
          </div>
        )}

        {step === 3 && (
          <div className="space-y-5">
            <p className="overline">Step 3 — The Vision</p>
            <h3 className="font-serif-display text-2xl md:text-3xl leading-tight">Tell us the story.</h3>
            <Field label="Occasion (optional)">
              <Input data-testid="ps-occasion" value={occasion} onChange={(e) => setOccasion(e.target.value)} placeholder="Anniversary, milestone, gift, family heirloom…" />
            </Field>
            <Field label="Notes for the studio">
              <Textarea data-testid="ps-notes" rows={5} value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Mood, palette, framing preference, who&apos;s in the photo…" />
            </Field>
          </div>
        )}

        {step === 4 && (
          <div className="space-y-5">
            <p className="overline">Step 4 — Confirm & Pay</p>
            <h3 className="font-serif-display text-2xl md:text-3xl leading-tight">Lock it in.</h3>
            <ul className="space-y-2 text-sm">
              <SummaryRow label="Name" value={name} />
              <SummaryRow label="Email" value={email} />
              <SummaryRow label="Phone" value={phone} />
              <SummaryRow label="Photo links" value={`${activeLinks.length} attached`} />
              {occasion && <SummaryRow label="Occasion" value={occasion} />}
            </ul>
            <div className="border border-[#D4AF37]/30 bg-[#824937]/20 p-5">
              <p className="overline text-[#D4AF37]">Initial Payment</p>
              <div className="flex items-baseline gap-3 mt-2">
                <span className="font-serif-display text-3xl text-[#D4AF37]">$50.00</span>
                <span className="text-xs uppercase tracking-[0.22em] text-white/60">Starts your Crowned experience</span>
              </div>
              <p className="text-white/65 text-sm mt-3 font-light">
                Remaining balance invoiced after proof approval, before we print. Test card: <code className="text-[#D4AF37]">4242 4242 4242 4242</code>.
              </p>
            </div>
          </div>
        )}
      </div>

      <div className="border-t border-white/10 p-5 md:p-6 flex items-center justify-between gap-3">
        <button type="button" onClick={goBack} disabled={step === 1 || submitting} data-testid="ps-back"
          className="text-xs uppercase tracking-[0.22em] text-white/60 hover:text-white disabled:opacity-30 disabled:cursor-not-allowed transition-colors">
          ← Back
        </button>
        {step < STEPS.length ? (
          <button type="button" onClick={goNext} data-testid="ps-next"
            className="group inline-flex items-center gap-2 bg-[#D4AF37] text-black px-6 py-3 text-xs uppercase tracking-[0.22em] font-semibold hover:bg-[#e6c245] transition-all">
            Continue<ArrowRight className="w-3.5 h-3.5 group-hover:translate-x-1 transition-transform" />
          </button>
        ) : (
          <button type="button" onClick={handleSubmitAndPay} disabled={submitting} data-testid="ps-pay"
            className="group inline-flex items-center gap-2 bg-[#D4AF37] text-black px-6 py-3 text-xs uppercase tracking-[0.22em] font-semibold hover:bg-[#e6c245] transition-all disabled:opacity-50">
            {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Pay $50 & Submit<Check className="w-4 h-4" /></>}
          </button>
        )}
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div className="space-y-2.5">
      <Label className="overline text-white/60">{label}</Label>
      <div className="[&>input]:bg-black [&>input]:border-white/15 [&>input]:h-14 [&>input]:rounded-none [&>input]:text-white [&>input]:focus-visible:ring-1 [&>input]:focus-visible:ring-[#D4AF37] [&>input]:focus-visible:border-[#D4AF37] [&>textarea]:bg-black [&>textarea]:border-white/15 [&>textarea]:rounded-none [&>textarea]:text-white [&>textarea]:focus-visible:ring-1 [&>textarea]:focus-visible:ring-[#D4AF37] [&>textarea]:focus-visible:border-[#D4AF37]">
        {children}
      </div>
    </div>
  );
}

function SummaryRow({ label, value }) {
  return (
    <li className="flex items-center justify-between gap-4 border-b border-white/5 py-2.5">
      <span className="overline text-white/50">{label}</span>
      <span className="text-white/85 text-sm text-right">{value || <span className="text-white/40">—</span>}</span>
    </li>
  );
}
